import React, { createContext, useContext } from 'react';
import {
  ChatLockResponse,
  GuardianChatParticipantCandidatesResponse,
  GuardianChatThreadItemResponse,
  GuardianChatThreadResponse,
} from '@mnspro-cloud/services/lib/mnspro-cloud/api/v2';
import { ChatCreateFormValues } from './ChatCreateDialog/ChatCreateDialog';
import { GuardianChatMessage, MetadataAttachment } from './GuardianChat.interface';

export type ChatLoadingState = 'idle' | 'loading' | 'loaded' | 'error';

export interface ChatContextValue {
  /** List of chat thread items for the thread list */
  threadItems: GuardianChatThreadItemResponse[];
  /** ID of the currently selected thread (set immediately on selection, before full data loads) */
  selectedThreadId: string | null;
  /** Full thread data for the selected thread (available after loading completes) */
  selectedThread: GuardianChatThreadResponse | null;
  /** Messages for the active thread */
  messages?: GuardianChatMessage[];
  /** The current user's ACS user ID */
  selfAcsUserId: string;
  /** Loading state of the thread list */
  threadListState: ChatLoadingState;
  /** Whether messages are currently loading */
  messagesLoadingState: ChatLoadingState;
  /**
   * Send a message in the active thread.
   * The optional `attachment` carries an already-uploaded attachment reference
   * (owned by the shared draft store) so the app layer does not need to hold the
   * pending attachment in its own store.
   */
  sendMessage: (message: string, attachment?: MetadataAttachment | null) => void;
  /** Select a thread from the list */
  selectThread: (threadItem: GuardianChatThreadItemResponse) => void;
  /** Deselect the current thread */
  deselectThread: () => void;
  /** Update a chat setting (lock configuration) */
  updateSettings?: (updatedSettings: ChatLockResponse) => void;
  /** List of all available users that can be added to a chat */
  participantCandidates: GuardianChatParticipantCandidatesResponse[];
  /** Call to load all available participants, that can be added to a chat (parents & teachers) */
  loadParticipantCandidates: () => void;
  /**
   * Indicates if all possible teachers and wards that may be
   * selected as a chat participant are currently being loaded
   */
  participantsLoadingState: ChatLoadingState;
  /**
   * Gets called, when the user wants to create a new chat thread
   */
  createChatThread?: (formValues: ChatCreateFormValues) => void;

  createChatDialogVisible?: boolean;

  setChatCreateDialogVisible?: (open: boolean) => void;

  /**
   * Opens or closes the "add participants" dialog.
   * The optional `showTeachers` argument controls whether the dialog includes
   * the teachers tab (true) or only the children tab (false). It is meaningful
   * only when opening (`open === true`).
   */
  setAddParticipantsDialogVisible?: (open: boolean, showTeachers?: boolean) => void;

  addParticipantsDialogVisible?: boolean;

  /**
   * Whether the "add participants" dialog should offer the teachers tab.
   * Controlled via `setAddParticipantsDialogVisible(true, showTeachers)`.
   */
  addParticipantsDialogShowTeachers?: boolean;

  addParticipantsToActiveThread?: (candidates: GuardianChatParticipantCandidatesResponse[]) => void;

  removeParticipantFromActiveThread?: (userId: string) => void;

  /** The current user represented as a participant candidate, used for preselection */
  currentUser?: GuardianChatParticipantCandidatesResponse;

  /**
   * User clicked on "load older messages" for the current thread
   */
  loadMessagesNextPage?: () => void;

  /**
   * Indicates, that there are more pages of previous messages for
   * the currently selected thread, that may be loaded.
   */
  hasMoreMessages?: boolean;

  deleteChat?: () => void;

  /**
   * Upload a file attachment and store the resulting file ID.
   * When called without a file, any previously uploaded attachment is removed,
   * so the next sent message will not include an attachment.
   */
  uploadFileAttachment?: (file?: File) => void;

  /** The uploaded file info after a successful file upload */
  uploadedFile?: MetadataAttachment | null;

  /** Download a file attachment by its ID */
  downloadAttachment?: (fileId: string, fileName: string) => void;
}

const ChatContext = createContext<ChatContextValue | null>(null);

export const ChatProvider: React.FC<React.PropsWithChildren<{ value: ChatContextValue }>> = ({
  value,
  children,
}) => {
  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
};

/**
 * Returns the ChatContext value. Throws if used outside a ChatProvider.
 */
export const useChatContext = (): ChatContextValue => {
  const context = useContext(ChatContext);
  if (!context) {
    throw new Error('useChatContext must be used within a ChatProvider');
  }
  return context;
};

/**
 * Returns the ChatContext value or null if no ChatProvider is present.
 * Use this in components that support both props-based and context-based usage.
 */
export const useChatContextOptional = (): ChatContextValue | null => {
  return useContext(ChatContext);
};
